import { useMemo } from 'react';
import useStores from '@stores/useStores';
import { useActionGuard, ActionType, ResourceType } from '@hooks/useActionGuard';
import { constructPermissionResult } from '@hooks/useActionGuard/utils';
import { getMessageList } from '@lib/utils/getMessageList';
import { ApiAccount } from '@api';

import { AccountResourceType } from '../../store';

/**
 * Custom hook that computes delete permissions for a list of accounts.
 * This hook pre-computes permissions at the component level to avoid calling hooks
 * inside loops, which violates React's Rules of Hooks.
 *
 * Data-retention accounts are gated on org-admin. The active storage account CAN be
 * deleted — that path runs through the Disconnect confirmation and clears the
 * workspace FK server-side; source/destination behavior (org-permission +
 * linked-resource guard) is preserved.
 *
 * @param accounts - Array of accounts to check permissions for
 * @param resourceType - The kind of resource the accounts belong to
 * @returns A map of account IDs to their delete permission results
 */
export const useDeleteAccountPermissions = (
  accounts: ApiAccount[],
  resourceType: AccountResourceType,
) => {
  const { userStore, permissionsStore, destinationsListStore, sourcesListStore } = useStores();
  const { canPerformAction, isNewPermissionModelEnforcementEnabled } = useActionGuard();

  const resourceLabel = resourceType === 'dataRetention' ? 'data retention' : resourceType;

  const dataRetentionAllowed = isNewPermissionModelEnforcementEnabled
    ? userStore.isOrgAdmin
    : permissionsStore.hasPermission({ resourceType: 'workspace', action: 'edit' });

  const sourceOrDestinationAllowed = canPerformAction({
    new: {
      actionType: ActionType.CREATE,
      resource: {
        type: resourceType === 'destination' ? ResourceType.DESTINATION : ResourceType.EVENT_STREAM,
      },
    },
    old: { resourceType: 'workspace', action: 'edit' },
  }).isAllowed;

  const isAllowed =
    resourceType === 'dataRetention' ? dataRetentionAllowed : sourceOrDestinationAllowed;

  const deleteAccountPermission = constructPermissionResult(
    isAllowed,
    `You do not have permission to delete ${resourceLabel} accounts.`,
  );

  const deletePermissionsMap = useMemo(
    () =>
      Object.fromEntries(
        accounts.map((account) => {
          let hasLinkedResources: boolean;
          let inUseReason: string;

          if (resourceType === 'destination') {
            hasLinkedResources = destinationsListStore.destinations.some(
              (resource) => resource.config.rudderAccountId === account.id,
            );
            inUseReason = 'You cannot delete an account with linked destinations.';
          } else if (resourceType === 'source') {
            hasLinkedResources = sourcesListStore.sources.some(
              (resource) => resource.config.rudderAccountId === account.id,
            );
            inUseReason = 'You cannot delete an account with linked sources.';
          } else {
            // Data-retention has a single workspace FK, not N linked resources. The
            // active account is deletable via the Disconnect confirmation (which
            // clears the FK), so nothing blocks deletion here.
            hasLinkedResources = false;
            inUseReason = '';
          }

          return [
            account.id,
            {
              isAllowed: deleteAccountPermission.isAllowed && !hasLinkedResources,
              denialReason: getMessageList([
                deleteAccountPermission.denialReason,
                hasLinkedResources ? inUseReason : '',
              ]),
            },
          ];
        }),
      ),
    [accounts],
  );

  return { deletePermissionsMap };
};
